import { useEffect, useMemo, useState } from "react";
import { FieldArray, Form, Formik } from "formik";
import { Alert, Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import * as yup from 'yup';
import { showError, showSuccess } from "../../../redux/counter/counterSlice";
import { useAppDispatch } from "../../../redux/store";
import { useVrboCreatePromotionMutation, useVrboUpdatePromotionMutation } from "../../../services/bClientApi";
import { CheckBoxInputField } from "../../FormFields/CheckBoxInput/CheckBoxInputField";
import { SelectInputField } from "../../FormFields/SelectInput/SelectInputField";
import { TextInputField } from "../../FormFields/TextInput/TextInputField";
import JSONData from "../../JSONData/JSONData";
import {
    buildCreatePromotionInput,
    buildPromotionValidationSchema,
    buildUpdatePromotionInput,
    emptyPromotionFormValues,
    promotionToFormValues,
} from "./vrboPromotionHelpers";
import { CREATABLE_PROMOTION_TYPES, DAYS_OF_WEEK, MEMBER_TIERS, VrboPromotion } from "./vrboPromotionTypes";

const STATUS_OPTIONS = [
    { value: 'ACTIVE', text: 'Active' },
    { value: 'INACTIVE', text: 'Inactive' },
]

const DISCOUNT_MODE_OPTIONS = [
    { value: 'single', text: 'Single discount' },
    { value: 'dayOfWeek', text: 'Day of week' },
    { value: 'tiered', text: 'Tiered by member level' },
]

const DISCOUNT_UNIT_OPTIONS = [
    { value: 'PERCENT', text: 'Percent (%)' },
    { value: 'AMOUNT', text: 'Amount ($)' },
]

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()

export default function VrboPromotionFormModal(
    props: {
        active: boolean
        propertyId: string
        // null creates a new promotion; a promotion edits it
        promotion: VrboPromotion | null
        onClose: () => void
        onSaved: () => void
    }
) {
    const { active, propertyId, promotion, onClose, onSaved } = props

    const dispatch = useAppDispatch()
    const [createPromotion] = useVrboCreatePromotionMutation()
    const [updatePromotion] = useVrboUpdatePromotionMutation()

    const [submitError, setSubmitError] = useState<any>(null)

    const isEdit = !!promotion

    useEffect(() => {
        if (active) {
            setSubmitError(null)
        }
    }, [active, promotion])

    const initialValues = useMemo(
        () => promotion ? promotionToFormValues(promotion) : emptyPromotionFormValues(),
        [promotion],
    )
    // raw JSON mode skips the form validation
    const validationSchema = useMemo(
        () => yup.lazy((values: any) => values?.useJson ? yup.object() : buildPromotionValidationSchema(isEdit)),
        [isEdit],
    )

    const typeOptions = isEdit
        ? [{ value: promotion.promotionType, text: promotion.promotionType }]
        : CREATABLE_PROMOTION_TYPES

    const save = async (promotionInput: any) => {
        setSubmitError(null)
        if (isEdit && !Object.keys(promotionInput).length) {
            dispatch(showError('No changes to save'))
            return
        }
        try {
            if (isEdit) {
                await updatePromotion({ propertyId, promotionId: promotion.id, promotion: promotionInput }).unwrap()
            } else {
                await createPromotion({ propertyId, promotion: promotionInput }).unwrap()
            }
            dispatch(showSuccess(isEdit ? 'Promotion updated' : 'Promotion created'))
            onSaved()
            onClose()
        } catch (err: any) {
            setSubmitError(err?.data || err)
            dispatch(showError(err?.data?.error || `Failed to ${isEdit ? 'update' : 'create'} promotion`))
        }
    }

    return (
        <Modal show={active} onHide={onClose} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{isEdit ? 'Edit Promotion' : 'New Promotion'}</Modal.Title>
            </Modal.Header>
            <Formik
                key={`${active}-${promotion?.id || 'new'}`}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting }) => {
                    try {
                        if (values.useJson) {
                            let parsed: any
                            try {
                                parsed = JSON.parse(values.jsonText)
                            } catch (error) {
                                setSubmitError({ error: 'Invalid JSON' })
                                return
                            }
                            await save(parsed)
                            return
                        }
                        await save(isEdit
                            ? buildUpdatePromotionInput(initialValues, values)
                            : buildCreatePromotionInput(values))
                    } finally {
                        setSubmitting(false)
                    }
                }}
            >
                {({ values, isSubmitting, setFieldValue }) => (
                    <Form>
                        <Modal.Body>
                            <div className="d-flex justify-content-end">
                                <CheckBoxInputField
                                    fieldName="useJson"
                                    fieldLabel="Advanced (raw JSON)"
                                    onChange={(e: any) => {
                                        if (e.target.checked) {
                                            const preview = isEdit
                                                ? buildUpdatePromotionInput(initialValues, values)
                                                : buildCreatePromotionInput(values)
                                            setFieldValue('jsonText', JSON.stringify(preview, null, 2))
                                        }
                                    }}
                                />
                            </div>

                            {values.useJson ? (
                                <>
                                    <Alert variant="info">
                                        {isEdit
                                            ? 'Send only the fields to change: status, incentive and/or restrictions.'
                                            : 'Send the full promotion: status, promotionType, incentive and restrictions (travelDateRange is required).'}
                                        {' '}Edits here are not reflected back in the form.
                                    </Alert>
                                    <TextInputField
                                        fieldName="jsonText"
                                        fieldLabel="Promotion JSON"
                                        placeholder="Promotion JSON"
                                        fieldType="textarea"
                                    />
                                </>
                            ) : (
                                <>
                                    <Row>
                                        <Col md={6}>
                                            <SelectInputField
                                                fieldName="promotionType"
                                                fieldLabel="Promotion type"
                                                placeholder="Select a type"
                                                options={typeOptions}
                                                disabled={isEdit}
                                                required
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <SelectInputField
                                                fieldName="status"
                                                fieldLabel="Status"
                                                placeholder="Select a status"
                                                options={STATUS_OPTIONS}
                                                required
                                            />
                                        </Col>
                                    </Row>

                                    <h6>Discount</h6>
                                    <Row>
                                        <Col md={6}>
                                            <SelectInputField
                                                fieldName="discountMode"
                                                fieldLabel="Discount type"
                                                placeholder="Select a discount type"
                                                options={DISCOUNT_MODE_OPTIONS}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <SelectInputField
                                                fieldName="discountUnit"
                                                fieldLabel="Unit"
                                                placeholder="Select a unit"
                                                options={DISCOUNT_UNIT_OPTIONS}
                                            />
                                        </Col>
                                    </Row>

                                    {values.discountMode === 'single' && (
                                        <TextInputField
                                            fieldName="singleValue"
                                            fieldLabel="Discount value"
                                            placeholder="Discount value"
                                            fieldType="number"
                                            required
                                        />
                                    )}

                                    {values.discountMode === 'dayOfWeek' && (
                                        <Row>
                                            {DAYS_OF_WEEK.map(day => (
                                                <Col md={3} sm={6} key={day}>
                                                    <TextInputField
                                                        fieldName={`dayOfWeek.${day}`}
                                                        fieldLabel={capitalize(day)}
                                                        placeholder={capitalize(day)}
                                                        fieldType="number"
                                                        required
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    )}

                                    {values.discountMode === 'tiered' && (
                                        <Row>
                                            {MEMBER_TIERS.map(tier => (
                                                <Col md={3} sm={6} key={tier}>
                                                    <TextInputField
                                                        fieldName={`tiers.${tier}`}
                                                        fieldLabel={capitalize(tier)}
                                                        placeholder={capitalize(tier)}
                                                        fieldType="number"
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                    )}

                                    <h6>Travel dates</h6>
                                    <Row>
                                        <Col md={6}>
                                            <TextInputField
                                                fieldName="travelFrom"
                                                fieldLabel="Travel from"
                                                placeholder="Travel from"
                                                fieldType="date"
                                                required
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <TextInputField
                                                fieldName="travelTo"
                                                fieldLabel="Travel to"
                                                placeholder="Travel to"
                                                fieldType="date"
                                                required
                                            />
                                        </Col>
                                    </Row>

                                    <FieldArray name="blackoutDates">
                                        {({ push, remove }) => (
                                            <div className="mb-3">
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <h6 className="mb-0">Blackout dates</h6>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() => push({ from: '', to: '' })}
                                                    >
                                                        Add blackout range
                                                    </Button>
                                                </div>
                                                {values.blackoutDates.map((_, index) => (
                                                    <Row key={index} className="align-items-start">
                                                        <Col md={5}>
                                                            <TextInputField
                                                                fieldName={`blackoutDates.${index}.from`}
                                                                fieldLabel="From"
                                                                placeholder="From"
                                                                fieldType="date"
                                                            />
                                                        </Col>
                                                        <Col md={5}>
                                                            <TextInputField
                                                                fieldName={`blackoutDates.${index}.to`}
                                                                fieldLabel="To"
                                                                placeholder="To"
                                                                fieldType="date"
                                                            />
                                                        </Col>
                                                        <Col md={2}>
                                                            <Button
                                                                variant="outline-danger"
                                                                className="mt-2"
                                                                size="sm"
                                                                onClick={() => remove(index)}
                                                            >
                                                                Remove
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                ))}
                                            </div>
                                        )}
                                    </FieldArray>

                                    <h6>Restrictions (optional)</h6>
                                    <Row>
                                        <Col md={3} sm={6}>
                                            <TextInputField fieldName="stayMin" fieldLabel="Min nights" placeholder="Min nights" fieldType="number" />
                                        </Col>
                                        <Col md={3} sm={6}>
                                            <TextInputField fieldName="stayMax" fieldLabel="Max nights" placeholder="Max nights" fieldType="number" />
                                        </Col>
                                        <Col md={3} sm={6}>
                                            <TextInputField fieldName="advanceMin" fieldLabel="Min days ahead" placeholder="Min days ahead" fieldType="number" />
                                        </Col>
                                        <Col md={3} sm={6}>
                                            <TextInputField fieldName="advanceMax" fieldLabel="Max days ahead" placeholder="Max days ahead" fieldType="number" />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <TextInputField
                                                fieldName="bookingFrom"
                                                fieldLabel="Bookable from"
                                                placeholder="Bookable from"
                                                fieldType="datetime-local"
                                                subText="Local property time"
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <TextInputField
                                                fieldName="bookingTo"
                                                fieldLabel="Bookable until"
                                                placeholder="Bookable until"
                                                fieldType="datetime-local"
                                                subText="Local property time"
                                            />
                                        </Col>
                                    </Row>
                                </>
                            )}

                            {submitError && (
                                <div className="mt-3">
                                    <JSONData data={submitError} title="Error saving promotion" onClose={() => setSubmitError(null)} />
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button type="submit" variant="primary" disabled={isSubmitting}>
                                {isSubmitting ? <Spinner animation="border" size="sm" /> : isEdit ? 'Save Changes' : 'Create Promotion'}
                            </Button>
                            <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                                Cancel
                            </Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    )
}
